import { UploadFile } from "antd";
import dayjs from "dayjs";
import {
  AssignmentDTO,
  AssignmentI,
  AssignmentsResponse,
  QuizStudentsOpts,
  QuizSubmitted,
  QuizTaken,
  ReleaseAssignmentDTO,
  updateGradeI,
} from "../interfaces/assignments.interface";
import { PaginateAssignments } from "../interfaces/assignments.interface";
import {
  assignmentsPayload,
  finalGradesPayload,
} from "../interfaces/course-grade.interface";
import http from "./axios";
import {
  addFilesToBody,
  convertObjectToFormData,
  downloadFile,
  handleError,
} from "./http-utils.service";
import { T } from "../interfaces/survey.interface";

export const getCourseAssignments = async (props: PaginateAssignments) => {
  const { courseId, limit, skip, query } = props;
  const options = {
    params: {
      limit,
      skip,
      ...(query ? { search: query } : {}),
    },
  };
  const response = await http.get<AssignmentsResponse>(
    `/courses/${courseId}/assignments`,
    options,
  );
  return response.data;
};

export const getLinkedAssignments = async (courseId: number | string) => {
  const response = await http.get<{
    assignments: AssignmentI[];
    distributions: {
      [key: string]: { pristine: boolean; positions: number[] };
    };
  }>(`/courses/${courseId}/linked-assignments`);
  return response.data;
};

export const getAvailableAssignmentsByCourse = async (courseId: string) => {
  const response = await http.get<AssignmentI[]>(
    `/courses/${courseId}/available-assignments`,
  );
  return response.data;
};

export const updateGradeAssignmentsByCourse = async (
  payload: assignmentsPayload,
  post?: boolean,
) => {
  let response;
  if (post) {
    response = await http.patch<updateGradeI>(
      `/assignments/${payload.id}`,
      payload,
    );
  } else {
    response = await http.post<updateGradeI>(`/assignments`, payload);
  }
  return response.data;
};

export const updateFinalGradesByCourse = async (
  payload: finalGradesPayload,
) => {
  const response = await http.patch<updateGradeI>(
    `/courses/${payload.courseId}/grades-details`,
    payload,
  );
  return response.data;
};

export const updateMultipleDistribution = async (payload: any) => {
  const response = await http.patch<updateGradeI>(`assignments/many`, payload);
  return response.data;
};

export const deleteGradeAssignment = async (assignmentId: number) => {
  const response = await http.delete<updateGradeI>(
    `assignments/${assignmentId}/grades`,
  );
  return response.data;
};

export const deleteAssignment = async (assignmentId: number) => {
  const response = await http.delete<AssignmentI>(
    `assignments/${assignmentId}`,
  );
  return response.data;
};

export const createAssignment = async ({
  info,
  files,
}: {
  info: AssignmentDTO;
  files: UploadFile[];
}) => {
  info.dueDates = info.dueDates?.map((dueDate) => {
    return { ...dueDate, date: dayjs(dueDate.date).toISOString() };
  });
  const formData = convertObjectToFormData(info);
  addFilesToBody(formData, files);

  const response = await http.post<{ success: boolean; data: AssignmentI }>(
    `/assignments`,
    formData,
    {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    },
  );
  return response.data;
};

export const updateAssignment = async (
  { info, files }: { info: AssignmentDTO; files: UploadFile[] },
  id: number,
) => {
  info.dueDates = info.dueDates?.map((dueDate) => {
    return { ...dueDate, date: dayjs(dueDate.date).toISOString() };
  });
  const formData = convertObjectToFormData(info);
  if (files?.length) {
    addFilesToBody(formData, files);
  }
  const response = await http.patch<{
    success: boolean;
    data: { assignment: AssignmentI };
  }>(`/assignments/${id}`, formData, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });
  return response.data;
};

export const downloadAssignmentFile = async (id: number, name: string) => {
  if (name === "") return false;

  try {
    const response = await http.get<{ url: string }>(
      `/assignments-files/${id}/download`,
      {
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
        },
      },
    );
    const res = await http.get<Blob>(response.data.url, {
      responseType: "blob",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
      },
    });

    downloadFile(res.data, name);
    return res.data;
  } catch (e) {
    throw handleError(e);
  }
};

export const assignmentAvailability = async (
  info: ReleaseAssignmentDTO,
  id: number,
) => {
  const response = await http.patch<{ success: boolean; data: AssignmentI }>(
    `/assignments/${id}/availability`,
    { ...info, id },
  );
  return response.data;
};

export const deleteAssignmentsFile = async (id: number) => {
  const response = await http.delete<{ success: boolean }>(
    `/assignments-files/${id}`,
  );
  return response.data;
};

export const getAssignmentById = async (id: number) => {
  try {
    const response = await http.get<any>(`/assignments/${id}`);
    return response.data;
  } catch (e) {
    throw handleError(e);
  }
};

export const postQuizSubmitted = async (id: number) => {
  const response = await http.post<QuizSubmitted>(`/assignments/${id}/submit`);
  return response.data;
};

export const patchQuizSubmitted = async (id: number, payload: QuizTaken) => {
  const response = await http.patch<QuizSubmitted>(
    `/assignments/${id}/submit`,
    payload,
  );
  return response.data;
};

export const getSummaryQuizByAssignmentId = async (id: number) => {
  try {
    const response = await http.get<QuizStudentsOpts>(
      `/assignments/${id}/submitted`,
    );
    return response.data;
  } catch (e) {
    throw handleError(e);
  }
};

export const patchQuizReviewed = async (id: number, payload: QuizTaken) => {
  const response = await http.patch<QuizSubmitted>(
    `/submitted-quizzes/${id}`,
    payload,
  );
  return response.data;
};

export const postGradeSubmitted = async (id: number) => {
  const response = await http.post<QuizSubmitted>(`/assignments/${id}/grades`);
  return response.data;
};

export const resetQuizSubmitted = async (submittedQuizId: number) => {
  const response = await http.delete<T>(`submitted-quizzes/${submittedQuizId}`);
  return response.data;
};
